/* ==========================================================================
   FIGS sets → garments. A set never goes in the bag as one line: the PDP's
   kit drawer, the PLP's set Quick Buy and the Uniform Builder each add one
   line per garment, and the bag (figs-cart.js) splits anything that still
   arrives as a set, or was saved as one, into the same lines.

   The garments and their prices match the Sets PDP (10-pdp-prototype/data.js
   SET_COMPONENTS / KITS); a set is recognised by its name. Every garment
   carries its on-model photo per colour (the bag shows that, never a ghost)
   and its ghost (the set Quick Buy's cards).
   ========================================================================== */

export const PDP = '/experiments/10-pdp-prototype/';
const APPAREL_11 = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
const APPAREL_7 = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL'];

export const slugOf = (s) => String(s || '').toLowerCase().replace(/\*/g, '-').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const garment = (c) => ({
  ghost: (slug) => `${PDP}assets/set/${c.id}/${slug}/ghost.webp`,
  model: (slug) => `${PDP}assets/set/${c.id}/${slug}/1.webp`,
  ...c,
});

/* `price` is each garment's live price — the kits' prices are these summed
   (Wide-Leg $100, the others $94) */
export const SET_COMPONENTS = {
  catarina: garment({ id: 'catarina', price: 42, role: 'Top', name: 'Catarina Scrub Top™', sizes: APPAREL_11, types: ['Regular', 'Petite'], typeLabel: 'Size Type', page: (c) => `${PDP}?color=${c}` }),
  rafaela: garment({
    id: 'rafaela', price: 42, role: 'Top', name: 'Rafaela Oversized Scrub Top™', sizes: APPAREL_11, types: ['Regular'], typeLabel: 'Size Type',
    page: () => 'https://www.wearfigs.com/products/womens-rafaela-scrub-top',
    ghost: (slug) => `${PDP}assets/xsell/rafaela/${slug}.webp`, // the Wear It With card's renders
  }),
  isabel: garment({ id: 'isabel', price: 58, role: 'Pant', name: 'Isabel High-Rise Wide-Leg Scrub Pant', sizes: APPAREL_7, types: ['Regular', 'Petite', 'Tall'], typeLabel: 'Length', page: (c) => `${PDP}pants.html?color=${c}` }),
  kade: garment({ id: 'kade', price: 52, role: 'Pant', name: 'Kade Straight-Leg Scrub Pant™', sizes: APPAREL_11, types: ['Regular', 'Petite', 'Tall'], typeLabel: 'Length', page: () => 'https://www.wearfigs.com/products/womens-kade-cargo-scrub-pants' }),
  zamora: garment({ id: 'zamora', price: 52, role: 'Pant', name: 'Zamora Jogger Scrub Pant™', sizes: APPAREL_11, types: ['Regular', 'Petite', 'Tall'], typeLabel: 'Length', page: () => 'https://www.wearfigs.com/products/womens-zamora-scrub-pants' }),
};

/* name → kit, on a card ("Jogger Set—Burgundy") or a saved line ("The
   Wide-Leg Set"). High-Waisted (Espresso) and Limited Edition (Bonsai) are
   the Wide-Leg Set in those colours, which is where their cards link. The
   Rafaela Set is the Rafaela top with the Kade straight leg ($42 + $52). */
export const SET_KITS = [
  { id: 'jogger', match: /^(the )?jogger set\b/i, components: ['catarina', 'zamora'] },
  { id: 'straight-leg', match: /^(the )?straight-leg set\b/i, components: ['catarina', 'kade'] },
  { id: 'wide-leg', match: /^(the )?(wide-leg|high-waisted|limited edition) set\b/i, components: ['catarina', 'isabel'] },
  { id: 'rafaela', match: /^(the )?rafaela set\b/i, components: ['rafaela', 'kade'] },
];
export const kitFor = (name) => SET_KITS.find((k) => k.match.test(String(name || '').replace(/^\s+/, '')));

/* a set that arrives as one item → its garments, in the set's colour and the
   item's size (a set saved as one line had one size for both) */
export function setParts(item) {
  const kit = kitFor(item?.name);
  if (!kit) return null;
  const slug = slugOf(item.color) || 'navy';
  return kit.components.map((id) => {
    const c = SET_COMPONENTS[id];
    return {
      id: c.id, name: c.name, color: item.color, size: item.size, type: '', price: c.price,
      qty: item.qty, image: c.model(slug), fallback: c.model('navy'),
    };
  });
}

/* a line saved before the bag went on-model (it carried a ghost) → the same
   product on model in its colour. The Uniform Builder's photos cover every
   garment the lab sells (wearfigs.com's shot of the colour, else its navy
   shot recoloured); the New Balance pairs have their own product photo. */
const UB = '/experiments/15-uniform-builder/assets/';
const ON_MODEL = new Set(['catarina', 'casma', 'rafaela', 'montex', 'zamora', 'zamora-hr', 'zamora-ruched', 'kade', 'kade-hr',
  'isabel', 'isabel-ruched', 'livingston', 'livingston-ruched', 'salta-ss', 'salta-ls', 'sydney', 'sydney-crop', 'scrub-cap']);
const ALIAS = { 'sydney-jacket': 'sydney' }; // the Sets PDP's Wear It With
export function onModelOf(line) {
  const id = ALIAS[line?.id] || line?.id;
  if (/^nb-/.test(id || '')) return { image: `${UB}products/${id}/01.webp`, fallback: '' };
  if (!ON_MODEL.has(id)) return null;
  return { image: `${UB}onmodel/${id}/${slugOf(line.color) || 'navy'}.webp`, fallback: `${UB}onmodel/${id}/navy.webp` };
}
